import {
  type AnswerChoice,
  type AnswerScopeRule,
  answerScopeRule,
  type FixedScopeReason,
} from "../../../skills/leadcue-reply/scripts/lib/answer-scope.ts";
import {
  contextValueSchemas,
  isContextKey,
} from "../../../skills/leadcue-reply/scripts/lib/context-contracts.ts";
import { REFERENCE_QUESTION } from "../../../skills/leadcue-reply/scripts/lib/materials.ts";

/**
 * What a question card says about who its answer reaches, before the rep saves it.
 *
 * Read from the host's own rule (`answer-scope.ts`), the one `answerRun` and the answer route use, so
 * the card cannot promise one reach while the answer is written at another
 * (docs/2026-09-27-question-card-answer-scope.md).
 */
export type AppliesTo =
  | { readonly kind: "choice"; readonly default: AnswerChoice }
  | { readonly kind: "fixed"; readonly reach: Reach; readonly reason: string };
/** Everyone an answer can reach, in the rep's terms. */
export type Reach = "conversation" | "campaign" | "person" | "you";

const REASONS = {
  live: "A booking or a free slot holds for one conversation only.",
  this_conversation: "Other conversations are not changed.",
  one_per_campaign:
    "A campaign has one answer for this, so it cannot differ between conversations.",
  campaign_only: "An event is set for the whole campaign.",
  about_you: "It describes you, not the lead.",
} as const satisfies Record<FixedScopeReason, string>;
const PERSON_REASON = "A time zone or a name belongs to a person, not to a campaign.";

/**
 * Null for a key no card answers: campaign configuration, or a key the host does not know. The
 * reference question (`materials.ts`), asked once per campaign, offers the campaign first.
 */
export function appliesTo(key: string, prompt?: string): AppliesTo | null {
  if (!isContextKey(key)) return null;
  if (key === "approved_facts" && prompt === REFERENCE_QUESTION)
    return { kind: "choice", default: "campaign" };
  const rule: AnswerScopeRule = answerScopeRule(key);
  switch (rule.kind) {
    case "configuration":
      return null;
    case "choice":
      return { kind: "choice", default: rule.default };
    case "person":
      return { kind: "fixed", reach: "person", reason: PERSON_REASON };
    case "fixed":
      return {
        kind: "fixed",
        reach: rule.scope === "thread" ? "conversation" : rule.scope === "rep" ? "you" : "campaign",
        reason: REASONS[rule.reason],
      };
  }
}

/** The reach in a phrase that completes "Applies to …". */
export function reachLabel(reach: Reach | AnswerChoice, campaignName: string | null): string {
  switch (reach) {
    case "thread":
    case "conversation":
      return "this conversation";
    case "campaign":
      return `every later lead in ${campaignName?.trim() || "this campaign"}`;
    case "person":
      return "the person who wrote this reply, in any conversation with them";
    case "you":
      return "you, in every conversation you answer";
  }
}

/**
 * What choosing each option does, said where the rep chooses it. The campaign is already named in
 * the option's own label, so these sentences do not repeat it.
 *
 * `heldIn` says where the campaign half lands. A typed card saves a Standing answer; the Yes/No
 * card, unchanged since 2026-09-19, writes the campaign's answer straight into drafting context,
 * so it must not promise a Standing answer it never makes.
 */
export function choiceConsequence(
  choice: AnswerChoice,
  heldIn: "standing_answers" | "campaign_context",
): string {
  if (choice === "thread")
    return "Only this conversation uses it. Another lead who needs it is asked again.";
  return heldIn === "standing_answers"
    ? "Later leads are told this without asking you. It is saved in Standing answers, where you can change or remove it."
    : "Later leads who ask the same question get this answer without asking you.";
}

/**
 * The Save button names who will be told, so the last thing pressed says it too. Null where the
 * reach is about a person or the rep and the line above already says so; the button keeps its
 * plain "Save answer".
 */
export function saveLabel(reach: Reach | AnswerChoice): string | null {
  switch (reach) {
    case "thread":
    case "conversation":
      return "Save for this conversation";
    case "campaign":
      return "Save for every later lead";
    case "person":
    case "you":
      return null;
  }
}

/**
 * Why "every later lead" cannot be chosen for this answer right now, or null when it can.
 *
 * A Standing answer holds typed text, so an answer given only as a file can be used for this
 * conversation but not saved for later leads; the route would report `answer_not_typed` after the
 * answer had landed. Saying so before Save is better than a note after it.
 */
export function campaignChoiceBlocked(
  key: string,
  typedText: string,
  hasUploads: boolean,
): string | null {
  if (appliesTo(key)?.kind !== "choice") return null;
  if (typedText.trim().length > 0 || !hasUploads) return null;
  return "A file is read for this conversation only. Type the answer to save it for every later lead.";
}

const TIME_ZONE_KEYS: ReadonlySet<string> = new Set(["recipient_timezone", "rep_timezone"]);

/**
 * What is wrong with a typed answer the key cannot hold, or null while it is empty or valid.
 *
 * Without this the card only disabled Save, and a rep who typed "London" for a time zone was left
 * guessing why (baseline critique, 2026-09-27).
 */
export function answerHint(key: string, typedText: string): string | null {
  const text = typedText.trim();
  if (text.length === 0 || !isContextKey(key)) return null;
  if (contextValueSchemas[key].safeParse(text).success) return null;
  return TIME_ZONE_KEYS.has(key)
    ? "Use a time zone name such as Europe/London or America/New_York."
    : "This detail cannot be saved in this form. Check the answer and try again.";
}
